// src/BookingsPage.js
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Chip,
  Alert,
  Button,
  TextField,
  IconButton,
  Grid,
  Avatar,
  Collapse,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import VerifiedIcon from '@mui/icons-material/Verified';
import RefreshIcon from '@mui/icons-material/Refresh';
import { supabase } from './supabaseClient';
import ProfileDialog from './ProfileDialog';
import BookingTracker from './BookingTracker';
import BookingAllowance from './BookingAllowance';
import BookingEnRouteMap from './BookingEnRouteMap';
import BookingDetailDialog from './BookingDetailDialog';
import { normalizeProfileImage } from './utils/imageUtils';

import { brand, ink, status, statusTone, surface } from './designTokens';
import { prettyStatus } from './labels';
// A booking is never stored as plain "Cancelled". The statuses are title-case
// with a suffix — "Cancelled by Client", "Cancelled by Stylist" — and a trigger
// normalises them that way, so every `status === 'Cancelled'` here matched
// nothing: the Cancelled tab was always empty and "Cancelled (This Month)"
// always read 0, while cancelled bookings sat in the list.
const isCancelled = (status) => String(status || '').toLowerCase().startsWith('cancelled');
// Never paid for, so never a booking — the row is only there because the
// payment intent needed a booking id to attach to.
const isExpired = (status) => String(status || '').toLowerCase() === 'expired';

export default function BookingsPage() {
  const [groupedBookings, setGroupedBookings] = useState([]);
  const [allGroupedBookings, setAllGroupedBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dialogPhone, setDialogPhone] = useState(null);
  // Shopping allowances, by booking id. Fetched alongside the bookings rather
  // than per row, so opening a booking does not cost a round trip.
  const [budgets, setBudgets] = useState({});
  // Stylists currently travelling to an appointment, by booking id. Rows exist
  // only while somebody is actually on the way, so this is usually empty.
  const [enRoute, setEnRoute] = useState({});
  const [detailBooking, setDetailBooking] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  // The tabs filter by where a booking is in its life, not by whether it was
  // paid for — so there was no way to answer "show me everything unpaid",
  // which is the question behind most money chases.
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [stats, setStats] = useState({
    upcoming: 0,
    pending: 0,
    completed: 0,
    cancelled: 0,
  });

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      // First try simple query - use booking_date or start_ts or created_at
      let { data, error: queryError } = await supabase
      .from('bookings')
      .select('*')
        .order('start_ts', { ascending: false });

      if (queryError) {
        console.error('Error fetching bookings:', queryError);
        setError(`Error: ${queryError.message}`);
        setGroupedBookings([]);
    setLoading(false);
        return;
      }

      // Allowances for every booking on screen, in one query.
      try {
        const { data: budgetRows } = await supabase
          .from('shopping_budgets')
          .select('*');
        const byBooking = {};
        (budgetRows || []).forEach((b) => {
          if (b.booking_id) byBooking[b.booking_id] = b;
        });
        setBudgets(byBooking);
      } catch (budgetErr) {
        // A booking without its allowance is still worth showing.
        console.warn('Could not load shopping allowances:', budgetErr);
      }

      try {
        const { data: enRouteRows } = await supabase
          .from('booking_en_route')
          .select('booking_id, state, latitude, longitude, updated_at');
        const byBooking = {};
        (enRouteRows || []).forEach((r) => {
          if (r.booking_id) byBooking[r.booking_id] = r;
        });
        setEnRoute(byBooking);
      } catch (enRouteErr) {
        console.warn('Could not load en-route positions:', enRouteErr);
      }

      if (!data || data.length === 0) {
        setGroupedBookings([]);
        setLoading(false);
      return;
    }

      // Fetch client profiles
      const clientIds = [...new Set(data.map(b => b.client_id).filter(Boolean))];
      const clientMap = new Map();
      if (clientIds.length > 0) {
        const { data: clients, error: clientsError } = await supabase
          .from('Profiles')
          .select('id, first_name, last_name, phone_number, email, profile_image, user_type, identity_verified')
          .in('id', clientIds);

        if (!clientsError && clients) {
          clients.forEach(client => {
            clientMap.set(client.id, { 
              ...client, 
              profile_image_url: normalizeProfileImage(client.profile_image),
            });
          });
        }
      }

      // Fetch ALL stylist profiles (same approach as ChatsPage) and map by phone number
      const stylistMapById = new Map();
      const stylistMapByPhone = new Map();
      
      // Fetch all profiles and map them (same as ChatsPage approach)
      const { data: allProfiles, error: profilesError } = await supabase
          .from('Profiles')
        .select('id, first_name, last_name, phone_number, email, profile_image, user_type, identity_verified');

      if (!profilesError && allProfiles) {
        allProfiles.forEach(profile => {
          if (profile.phone_number) {
            // Store with original phone number
            stylistMapByPhone.set(profile.phone_number, {
              ...profile,
              profile_image_url: normalizeProfileImage(profile.profile_image),
            });
            // Also store with normalized phone number for better matching
            const normalizedPhone = profile.phone_number.toString().replace(/[\s\-().+]/g, '').toLowerCase();
            if (normalizedPhone && normalizedPhone !== profile.phone_number) {
              stylistMapByPhone.set(normalizedPhone, {
                ...profile,
                profile_image_url: normalizeProfileImage(profile.profile_image),
              });
            }
          }
          if (profile.id) {
            stylistMapById.set(profile.id, {
              ...profile,
              profile_image_url: normalizeProfileImage(profile.profile_image),
            });
          }
        });
      }

      // Group bookings by client phone number
      const bookingsByUser = new Map();

      data.forEach(booking => {
        const client = booking.client_id ? clientMap.get(booking.client_id) : null;
        const phone = client?.phone_number || booking.client_phone_number || 'Unknown';
        
        if (!bookingsByUser.has(phone)) {
          bookingsByUser.set(phone, {
            phone,
            client: client || {},
            bookings: [],
          });
        }

        // Get stylist by phone number - try multiple column name variations
        const stylistPhone = booking.stylist_phone_number || booking.stylists_phone_number || booking.stylist_phone || null;
        let stylist = null;
        
        if (stylistPhone) {
          // First try exact match
          stylist = stylistMapByPhone.get(stylistPhone);
          
          // If not found, try normalized phone comparison (remove spaces, dashes, parentheses, etc.)
          if (!stylist) {
            const normalizedStylistPhone = stylistPhone.toString().replace(/[\s\-().+]/g, '').toLowerCase();
            for (const [phone, profile] of stylistMapByPhone.entries()) {
              if (phone) {
                const normalizedPhone = phone.toString().replace(/[\s\-().+]/g, '').toLowerCase();
                if (normalizedPhone && normalizedStylistPhone && normalizedPhone === normalizedStylistPhone) {
                  stylist = profile;
                  console.log('BookingsPage: Found stylist via normalized phone match:', stylistPhone, '->', phone);
                  break;
                }
              }
            }
          }
          
          // If still not found, try by ID as fallback
          if (!stylist && booking.stylist_profile_id) {
            stylist = stylistMapById.get(booking.stylist_profile_id);
            if (stylist) {
              console.log('BookingsPage: Found stylist via ID:', booking.stylist_profile_id);
            }
          }
          
          // Debug: Log if stylist not found
          if (!stylist) {
            console.log('BookingsPage: Stylist not found for phone:', stylistPhone, 'Booking ID:', booking.id);
            console.log('BookingsPage: Available profile phones (first 10):', Array.from(stylistMapByPhone.keys()).slice(0, 10));
          } else {
            console.log('BookingsPage: Found stylist:', stylist.first_name, stylist.last_name, 'for phone:', stylistPhone);
          }
        }

        const bookingWithProfiles = {
          ...booking,
          client: client,
          stylist: stylist || null,
        };

        bookingsByUser.get(phone).bookings.push(bookingWithProfiles);
      });

      // Convert to array and sort
      const grouped = Array.from(bookingsByUser.values())
        .map(group => ({
          ...group,
          bookings: group.bookings.sort((a, b) => 
            new Date(b.start_ts || b.booking_date || b.created_at) - new Date(a.start_ts || a.booking_date || a.created_at)
          ),
        }))
        .sort((a, b) => 
          new Date(b.bookings[0]?.start_ts || b.bookings[0]?.booking_date || b.bookings[0]?.created_at) - 
          new Date(a.bookings[0]?.start_ts || a.bookings[0]?.booking_date || a.bookings[0]?.created_at)
        );

      setGroupedBookings(grouped);
      setAllGroupedBookings(grouped);

      // Calculate stats from grouped bookings
      const now = new Date();
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      
      // Flatten bookings for stats calculation
      const allBookings = grouped.flatMap(group => group.bookings);
      
      // "Not cancelled and not completed" also admitted Expired, Declined and
      // Payment Failed, so one real appointment plus two abandoned payment
      // screens read as three upcoming bookings. Say which statuses are live
      // instead of which ones are dead.
      const LIVE_STATUSES = ['Requested', 'Confirmed', 'Pending Payment'];
      const upcoming = allBookings.filter(b => {
        const bookingDate = b.start_ts ? new Date(b.start_ts) : null;
        return bookingDate && bookingDate > now && LIVE_STATUSES.includes(b.status);
      }).length;

      // 'Pending' and 'Pending Confirmation' are not statuses this database
      // has ever held — the check constraint would reject them — so this tile
      // read 0 whatever was waiting. The real ones are these two.
      const pending = allBookings.filter(
        b => b.status === 'Pending Payment' || b.status === 'Requested'
      ).length;

      const completedThisMonth = allBookings.filter(b => {
        const bookingDate = b.start_ts ? new Date(b.start_ts) : null;
        return b.status === 'Completed' && bookingDate && bookingDate >= firstDayOfMonth;
      }).length;

      const cancelledThisMonth = allBookings.filter(b => {
        const bookingDate = b.start_ts ? new Date(b.start_ts) : null;
        return isCancelled(b.status) && bookingDate && bookingDate >= firstDayOfMonth;
      }).length;

      setStats({
        upcoming,
        pending,
        completed: completedThisMonth,
        cancelled: cancelledThisMonth,
      });
    } catch (err) {
      console.error('Error fetching bookings:', err);
      setError(`Error: ${err.message || 'Failed to fetch bookings'}`);
      setGroupedBookings([]);
      setAllGroupedBookings([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter and search
  // Live, without a refresh button.
  //
  // A booking changes because of something happening elsewhere — a stylist
  // accepting on her phone, a Stripe hold being captured — so the admin looking
  // at this page has no reason to know it is now stale. Postgres tells us
  // instead.
  //
  // Three tables are watched: bookings for the tracker, shopping_budgets for
  // the allowance, since money can move without the booking's own status
  // changing, and booking_en_route for a stylist's position.
  useEffect(() => {
    const channel = supabase
      .channel('bookings-live')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'bookings' }, () => {
        fetchData();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'shopping_budgets' }, () => {
        fetchData();
      })
      // A position ping lands every twenty seconds or so per travelling
      // stylist. Refetching every booking and profile each time would be
      // absurd for a moved pin, so this one patches just the pin.
      .on('postgres_changes', { event: '*', schema: 'public', table: 'booking_en_route' }, (payload) => {
        const row = payload.new && payload.new.booking_id ? payload.new : null;
        const gone = payload.old && payload.old.booking_id ? payload.old : null;
        setEnRoute((current) => {
          const next = { ...current };
          if (payload.eventType === 'DELETE' && gone) {
            delete next[gone.booking_id];
          } else if (row) {
            next[row.booking_id] = row;
          }
          return next;
        });
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
    // fetchData is stable for this page's lifetime; re-subscribing on every
    // render would tear the socket down and rebuild it constantly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let filtered = [...allGroupedBookings];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(group => {
        const matchesName = `${group.client?.first_name || ''} ${group.client?.last_name || ''}`.toLowerCase().includes(term);
        const matchesPhone = group.phone?.toLowerCase().includes(term);
        const matchesEmail = group.client?.email?.toLowerCase().includes(term);
        const matchesService = group.bookings.some(b =>
          b.service_title?.toLowerCase().includes(term)
        );
        const matchesStylist = group.bookings.some(b => 
          `${b.stylist?.first_name || ''} ${b.stylist?.last_name || ''}`.toLowerCase().includes(term)
        );
        // By booking id too. The search covered five fields and not this one,
        // which is the field a support ticket actually quotes — someone holding
        // a booking id had no way to find it here. Matched as a prefix so a
        // short fragment of a uuid is enough to type.
        const matchesId = group.bookings.some(b =>
          String(b.id || '').toLowerCase().startsWith(term) ||
          String(b.id || '').toLowerCase().includes(term)
        );
        return matchesName || matchesPhone || matchesEmail || matchesService || matchesStylist || matchesId;
      });
    }

    // Apply tab filter
    if (paymentFilter !== 'all') {
      filtered = filtered.filter(group => group.bookings.some(b => {
        const paid = b.payment_completed === true;
        const refunded = String(b.status || '').toLowerCase().includes('refund');
        if (paymentFilter === 'paid') return paid && !refunded;
        if (paymentFilter === 'unpaid') return !paid;
        if (paymentFilter === 'refunded') return refunded;
        return true;
      }));
    }

    if (activeTab === 'expired') {
      filtered = filtered.map(group => ({
        ...group,
        bookings: group.bookings.filter(b => isExpired(b.status)),
      })).filter(group => group.bookings.length > 0);
    } else if (activeTab === 'upcoming') {
      const now = new Date();
      filtered = filtered.map(group => ({
        ...group,
        bookings: group.bookings.filter(b => {
        const bookingDate = b.start_ts ? new Date(b.start_ts) : null;
        return bookingDate && bookingDate > now && !isCancelled(b.status) && b.status !== 'Completed';
        }),
      })).filter(group => group.bookings.length > 0);
    } else if (activeTab === 'completed') {
      filtered = filtered.map(group => ({
        ...group,
        bookings: group.bookings.filter(b => b.status === 'Completed'),
      })).filter(group => group.bookings.length > 0);
    } else if (activeTab === 'cancelled') {
      filtered = filtered.map(group => ({
        ...group,
        bookings: group.bookings.filter(b => isCancelled(b.status)),
      })).filter(group => group.bookings.length > 0);
    } else {
      filtered = filtered.map(group => ({
        ...group,
        bookings: group.bookings.filter(b => !isExpired(b.status)),
      })).filter(group => group.bookings.length > 0);
    }

    setGroupedBookings(filtered);
  }, [searchTerm, activeTab, paymentFilter, allGroupedBookings]);

  useEffect(() => {
    fetchData();

    // Live-update the list when a booking is created/changed elsewhere
    // (client app, another admin tab) instead of requiring a manual refresh.
    const channel = supabase
      .channel('bookings-page-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'bookings' }, () => {
        fetchData();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Profile dialog
  const closeProfile = () => setDialogPhone(null);

  // Delegates to the shared statusTone() so every screen renders the same
  // status identically. (The parameter was named `status`, which shadowed the
  // imported token of the same name — every colour here resolved to undefined.)
  const getStatusChipColor = (value) => {
    const v = String(value || '').toLowerCase();
    if (v === 'upcoming') return { bg: brand.goldWash, color: brand.goldDeep };
    const tone = statusTone(value);
    return { bg: tone.bg, color: tone.fg };
  };

  const getUserTypeColor = (userType) => {
    switch (userType?.toLowerCase()) {
      case 'stylist':
        return 'primary';
      case 'client':
        return 'success';
      case 'admin':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const toggleRow = (rowId) => {
    const rowIdString = String(rowId);
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(rowIdString)) {
      newExpanded.delete(rowIdString);
    } else {
      newExpanded.add(rowIdString);
    }
    setExpandedRows(newExpanded);
  };

  return (
    <Box sx={{ p: 4, bgcolor: surface.page, minHeight: '100vh' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Overview Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 2.5, 
              borderRadius: 3,
              border: `1px solid ${surface.line}`,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <Box sx={{ p: 1.5, borderRadius: '50%', bgcolor: brand.goldWash, mr: 2 }}>
              <CalendarTodayIcon sx={{ color: brand.gold, fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ color: ink[500], mb: 0.5 }}>
                Upcoming Bookings
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {stats.upcoming}
              </Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 2.5, 
              borderRadius: 3,
              border: `1px solid ${surface.line}`,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <Box sx={{ p: 1.5, borderRadius: '50%', bgcolor: status.warningBg, mr: 2 }}>
              <HourglassEmptyIcon sx={{ color: status.warning, fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ color: ink[500], mb: 0.5 }}>
                Pending Confirmation
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {stats.pending}
              </Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 2.5, 
              borderRadius: 3,
              border: `1px solid ${surface.line}`,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <Box sx={{ p: 1.5, borderRadius: '50%', bgcolor: status.successBg, mr: 2 }}>
              <CheckCircleIcon sx={{ color: status.success, fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ color: ink[500], mb: 0.5 }}>
                Completed (This Month)
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {stats.completed}
              </Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 2.5, 
              borderRadius: 3,
              border: `1px solid ${surface.line}`,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <Box sx={{ p: 1.5, borderRadius: '50%', bgcolor: status.dangerBg, mr: 2 }}>
              <CancelIcon sx={{ color: status.danger, fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ color: ink[500], mb: 0.5 }}>
                Cancelled (This Month)
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {stats.cancelled}
      </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Bookings Table Section */}
      <Paper 
        elevation={0}
        sx={{ 
          borderRadius: 3,
          border: `1px solid ${surface.line}`,
          overflow: 'hidden'
        }}
      >
        {/* Table Header with Tabs */}
        <Box sx={{ p: 3, borderBottom: `1px solid ${surface.line}` }}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            flexWrap: 'wrap',
            rowGap: 1.5,
          }}>
            <Box sx={{
              display: 'flex',
              gap: 4,
              borderBottom: `1px solid ${surface.line}`,
              overflowX: 'auto',
              maxWidth: '100%',
              '&::-webkit-scrollbar': { height: 0 },
            }}>
              <Button
                onClick={() => setActiveTab('all')}
                sx={{
                  py: 1.5,
                  px: 0,
                  minWidth: 'auto',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  color: activeTab === 'all' ? ink[900] : ink[500],
                  borderBottom: activeTab === 'all' ? `2px solid ${ink[900]}` : '2px solid transparent',
                  borderRadius: 0,
                  fontWeight: activeTab === 'all' ? 600 : 500,
                  textTransform: 'none',
                  '&:hover': { bgcolor: 'transparent', color: ink[900] }
                }}
              >
                All Bookings
              </Button>
              <Button
                onClick={() => setActiveTab('upcoming')}
                sx={{
                  py: 1.5,
                  px: 0,
                  minWidth: 'auto',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  color: activeTab === 'upcoming' ? ink[900] : ink[500],
                  borderBottom: activeTab === 'upcoming' ? `2px solid ${ink[900]}` : '2px solid transparent',
                  borderRadius: 0,
                  fontWeight: activeTab === 'upcoming' ? 600 : 500,
                  textTransform: 'none',
                  '&:hover': { bgcolor: 'transparent', color: ink[900] }
                }}
              >
                Upcoming
              </Button>
              <Button
                onClick={() => setActiveTab('completed')}
                sx={{
                  py: 1.5,
                  px: 0,
                  minWidth: 'auto',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  color: activeTab === 'completed' ? ink[900] : ink[500],
                  borderBottom: activeTab === 'completed' ? `2px solid ${ink[900]}` : '2px solid transparent',
                  borderRadius: 0,
                  fontWeight: activeTab === 'completed' ? 600 : 500,
                  textTransform: 'none',
                  '&:hover': { bgcolor: 'transparent', color: ink[900] }
                }}
              >
                Completed
              </Button>
              <Button
                onClick={() => setActiveTab('cancelled')}
                sx={{
                  py: 1.5,
                  px: 0,
                  minWidth: 'auto',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  color: activeTab === 'cancelled' ? ink[900] : ink[500],
                  borderBottom: activeTab === 'cancelled' ? `2px solid ${ink[900]}` : '2px solid transparent',
                  borderRadius: 0,
                  fontWeight: activeTab === 'cancelled' ? 600 : 500,
                  textTransform: 'none',
                  '&:hover': { bgcolor: 'transparent', color: ink[900] }
                }}
              >
                Cancelled
              </Button>
              <Button
                onClick={() => setActiveTab('expired')}
                sx={{
                  py: 1.5,
                  px: 0,
                  minWidth: 'auto',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  color: activeTab === 'expired' ? ink[900] : ink[500],
                  borderBottom: activeTab === 'expired' ? `2px solid ${ink[900]}` : '2px solid transparent',
                  borderRadius: 0,
                  fontWeight: activeTab === 'expired' ? 600 : 500,
                  textTransform: 'none',
                  '&:hover': { bgcolor: 'transparent', color: ink[900] }
                }}
              >
                Unpaid / Expired
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', rowGap: 1.5 }}>
              <Box sx={{ position: 'relative' }}>
                <SearchIcon 
                  sx={{ 
                    position: 'absolute', 
                    left: 12, 
                    top: '50%', 
                    transform: 'translateY(-50%)',
                    color: ink[300],
                    fontSize: 20
                  }} 
                />
                <TextField
                  select
                  size="small"
                  label="Payment"
                  value={paymentFilter}
                  onChange={(e) => setPaymentFilter(e.target.value)}
                  sx={{ width: 150, mr: 1 }}
                  SelectProps={{ native: true }}
                  InputLabelProps={{ shrink: true }}
                >
                  <option value="all">All</option>
                  <option value="paid">Paid</option>
                  <option value="unpaid">Unpaid</option>
                  <option value="refunded">Refunded</option>
                </TextField>
                <TextField
                  placeholder="Search bookings or paste a booking ID..."
                  size="small"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{
                    width: 256,
                    '& .MuiOutlinedInput-root': {
                      pl: 4.5,
                      '& fieldset': {
                        borderColor: surface.line,
                      },
                    },
                  }}
                />
              </Box>
              <Chip label="Live" size="small" color="success" variant="outlined" sx={{ height: 24, fontSize: '0.7rem' }} />
              <IconButton onClick={fetchData} sx={{ color: ink[500] }}>
                <RefreshIcon />
              </IconButton>
            </Box>
          </Box>
        </Box>

        {/* Collapsible Rows */}
              {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
          </Box>
        ) : groupedBookings.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="textSecondary" gutterBottom>
              No Bookings Found
            </Typography>
            <Typography variant="body2" color="textSecondary">
              No bookings found in the database.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ overflow: 'hidden' }}>
            {groupedBookings.map((group, idx) => {
              const rowId = group.phone || `user-${idx}`;
              const isExpanded = expandedRows.has(String(rowId));
              const userName = `${group.client?.first_name || ''} ${group.client?.last_name || ''}`.trim() || 'Unknown';
              const profileImage = group.client?.profile_image_url || group.client?.profile_image;
              const isVerified = group.client?.identity_verified === true || group.client?.identity_verified === 'true' || group.client?.identity_verified === 1;

              return (
                <Box key={rowId}>
                  {/* Summary Row */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      p: 2,
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'action.hover' },
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                    onClick={() => toggleRow(rowId)}
                  >
                    {/* Profile Image */}
                    <Box sx={{ mr: 2, flexShrink: 0 }}>
                      {profileImage ? (
                        <Avatar
                          src={profileImage}
                          alt={userName}
                          sx={{ width: 48, height: 48 }}
                        />
                      ) : (
                        <Avatar sx={{ width: 48, height: 48, bgcolor: 'grey.300' }}>
                          {userName !== 'Unknown' ? userName.charAt(0).toUpperCase() : '?'}
                        </Avatar>
                      )}
                    </Box>

                    {/* User Info */}
                    <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 3, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 150, flexShrink: 0 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {userName}
                        </Typography>
                        {isVerified && (
                          <VerifiedIcon color="primary" fontSize="small" />
                        )}
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 200, flexShrink: 0 }}>
                        {group.client?.email || 'N/A'}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ minWidth: 150, flexShrink: 0, cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (group.phone) setDialogPhone(group.phone);
                        }}
                      >
                        {group.phone || 'N/A'}
                      </Typography>
                      <Chip
                        label={prettyStatus(group.client?.user_type) || 'N/A'}
                        color={getUserTypeColor(group.client?.user_type)}
                        size="small"
                        sx={{ minWidth: 80, flexShrink: 0 }}
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100, flexShrink: 0 }}>
                        {group.bookings.length} booking{group.bookings.length !== 1 ? 's' : ''}
                      </Typography>
                    </Box>

                    {/* Expand/Collapse Icon */}
                    <IconButton size="small" sx={{ flexShrink: 0 }}>
                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>

                  {/* Expanded Bookings */}
                  <Collapse in={isExpanded}>
                    <Box sx={{ p: 3, backgroundColor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Bookings ({group.bookings.length})
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Grid container spacing={2}>
                        {group.bookings.map((booking) => {
                  const statusColors = getStatusChipColor(booking.status);
                  const bookingDate = booking.start_ts ? new Date(booking.start_ts) : null;
                  const dateStr = bookingDate ? bookingDate.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  }) : 'N/A';
                  const timeStr = bookingDate ? bookingDate.toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit'
                  }) : 'N/A';
                  
                    return (
                            <Grid item xs={12} key={booking.id}>
                              <Paper elevation={1} sx={{ p: 2 }}>
                                {/* Where this booking has got to, and what is
                                    left of the client's allowance. Both update
                                    on their own — see the realtime subscription
                                    in the effect below. */}
                                <BookingTracker status={booking.status} />
                                {/* Only rendered while a stylist is actually
                                    travelling to this one. */}
                                <BookingEnRouteMap
                                  position={enRoute[booking.id]}
                                  brand={brand}
                                  ink={ink}
                                />
                                <Box sx={{ mb: 2, mt: 1 }}>
                                  <BookingAllowance budget={budgets[booking.id]} />
                                </Box>
                                <Divider sx={{ mb: 2 }} />
                                <Grid container spacing={2} alignItems="center">
                                  <Grid item xs={12} sm={2}>
                                    <Typography variant="caption" color="text.secondary">
                                      Booking ID
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      onClick={() => setDetailBooking(booking)}
                                      sx={{
                                        fontFamily: 'monospace',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        color: brand.gold,
                                        '&:hover': { textDecoration: 'underline' },
                                      }}
                                    >
                        #{booking.id?.slice(0, 8).toUpperCase() || 'N/A'}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12} sm={2}>
                                    <Typography variant="caption" color="text.secondary">
                                      Date & Time
                                    </Typography>
                                    <Typography variant="body2">
                                      {dateStr} - {timeStr}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12} sm={2}>
                                    <Typography variant="caption" color="text.secondary">
                                      Service
                                    </Typography>
                                    <Typography variant="body2">
                                      {booking.service_title || 'N/A'}
                            </Typography>
                                  </Grid>
                                  <Grid item xs={12} sm={2}>
                                    <Typography variant="caption" color="text.secondary">
                                      Stylist
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Typography variant="body2">
                                        {booking.stylist 
                                          ? `${booking.stylist.first_name} ${booking.stylist.last_name}`.trim() 
                                          : (booking.stylist_phone_number || booking.stylists_phone_number || booking.stylist_phone || 'N/A')}
                                      </Typography>
                                      {booking.stylist && (booking.stylist.identity_verified === true || booking.stylist.identity_verified === 'true' || booking.stylist.identity_verified === 1) && (
                                        <VerifiedIcon color="primary" fontSize="small" />
                                      )}
                                    </Box>
                                  </Grid>
                                  <Grid item xs={12} sm={2}>
                                    <Typography variant="caption" color="text.secondary">
                                      Amount
                            </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        ${parseFloat(booking.price || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12} sm={2}>
                        <Chip
                          label={prettyStatus(booking.status) || 'Pending'}
                          size="small"
                      sx={{
                            bgcolor: statusColors.bg,
                            color: statusColors.color,
                            fontWeight: 500,
                            fontSize: '0.75rem',
                                      }}
                                    />
                                  </Grid>
                                  {(booking.meeting_place_name || booking.meeting_address) && (
                                    <Grid item xs={12}>
                                      <Typography variant="caption" color="text.secondary">
                                        Where
                                      </Typography>
                                      <Typography variant="body2">
                                        {booking.meeting_place_name || booking.meeting_address}
                                      </Typography>
                                      {booking.meeting_place_name && booking.meeting_address && (
                                        <Typography variant="caption" color="text.secondary">
                                          {booking.meeting_address}
                                        </Typography>
                                      )}
                                    </Grid>
                                  )}
                                </Grid>
                              </Paper>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Box>
                  </Collapse>
            </Box>
              );
            })}
          </Box>
        )}
      </Paper>

      <BookingDetailDialog
        booking={detailBooking}
        budget={detailBooking ? budgets[detailBooking.id] : null}
        open={!!detailBooking}
        onClose={() => setDetailBooking(null)}
        onSaved={() => { setDetailBooking(null); fetchData(); }}
      />

      <ProfileDialog
        open={Boolean(dialogPhone)}
        phoneNumber={dialogPhone}
        onClose={closeProfile}
      />
    </Box>
  );
}
